import type { FailCodePart } from '@packages/models/api';
import type React from 'react';

import { FailCodeDetails } from '@packages/ui/qmr';
import { Button, Icon, Label, ListButton, Typography } from '@packages/ui/shared';
import Select from '@web/components/select';
import { groupBy } from 'lodash';
import { Fragment, type Ref, useCallback } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import { AsyncTypeahead, Highlighter, Menu, MenuItem, type TypeaheadMenuProps } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { FailCodeSelection, FailCodeVehicleParams } from './fail-codes-modal.types';

import { useSearchByPart } from './hooks/use-search-by-part';

/**
 * Props for the {@link SearchByPart} component.
 */
export type SearchByPartProps = {
    /** Callback to navigate back to the search method selector. */
    onBack: () => void;

    /** Callback to close the fail codes modal entirely. */
    onClose: () => void;

    /** Callback to confirm the selected fail code and persist it on the case. */
    onConfirmFailCode: (selection: FailCodeSelection) => void;

    /** Whether to prefill the first part number result on mount (used by "View matching fail codes"). */
    prefillPartNumber?: boolean;

    /** Vehicle params used to scope all lookups, already mapped from VIN data by the parent. */
    vehicleParams: FailCodeVehicleParams;
};

/**
 * Search-by-part fail code selection component. Provides a multi-step flow: search for parts,
 * select a part, choose a fail code (auto-selected if only one), then confirm section and
 * subsection (auto-resolved when unambiguous).
 *
 * This is a presentational shell; all state management and API interactions are delegated to
 * the {@link useSearchByPart} hook.
 *
 * @param props - {@link SearchByPartProps}
 * @returns the search by part JSX
 */
const useSearchByPartStyles = createUseStyles({
    buttonRow: {
        display: 'flex',
        gap: 15,
    },
});

/**
 * SearchByPart component renders the fail code search flow by part number.
 * Guides the user through searching for a part, selecting a fail code, and confirming section/subsection.
 *
 * @param props - SearchByPartProps
 * @returns JSX.Element
 */
export function SearchByPart({
    onBack,
    onClose,
    onConfirmFailCode,
    prefillPartNumber,
    vehicleParams,
}: SearchByPartProps) {
    // Hooks
    const classes = useSearchByPartStyles();
    const { t } = useTranslation();

    // Self-contained read-only exception: this hook performs only lookups (part search, fail code
    // lookup, section/subsection resolution). It never mutates shared domain state. The parent
    // receives the final result via onConfirmFailCode. All intermediate cascade state is managed
    // internally.
    const {
        autoSelectedSectionName,
        autoSelectedSubsectionName,
        confirmSelection,
        confirmedCodeSelection,
        handleBack,
        handleConfirmFailCodeSelection,
        handleFailCodeSelect,
        handleInputChange,
        handlePartSearch,
        handlePartSelect,
        handleSectionChange,
        handleSubsectionChange,
        isAddDisabled,
        isLoading,
        isOnlyMatch,
        isSelectDisabled,
        partFailCodeData,
        partOptionData,
        searchInputRef,
        sectionOptionData,
        selectedFailCodeSection,
        selectedFailCodeSubsection,
        selectedPartFailCode,
        selectedPartOption,
        showSectionSelects,
        subsectionOptionData,
    } = useSearchByPart({
        prefillPartNumber,
        vehicleParams,
    });

    // Handlers

    /**
     * Handle the typeahead selection event. Extracts the first selected item and forwards
     * it to the hook.
     *
     * @param selected - the array of selected typeahead items
     */
    const handleTypeaheadChange = useCallback(
        (selected: FailCodePart[]) => {
            handlePartSelect(selected[0]);
        },
        [handlePartSelect]
    );

    /**
     * Handle the "Back" button press. Delegates to the hook first; if the hook did not
     * handle it (user is on the first step), navigates back to the search method selector.
     */
    const handleBackButtonClick = useCallback(() => {
        const handledInternally = handleBack();
        if (!handledInternally) {
            onBack();
        }
    }, [handleBack, onBack]);

    /**
     * Handle the "Select Fail Code" / "Add Fail Code" button press. On the fail code picker
     * step, confirms the selection. On the section/subsection step, builds the final selection
     * and calls the parent callback.
     */
    const handleSubmitButtonClick = useCallback(() => {
        if (!confirmedCodeSelection) {
            handleConfirmFailCodeSelection();
            return;
        }

        const selection = confirmSelection();
        if (selection) {
            onConfirmFailCode(selection);
            onClose();
        }
    }, [confirmSelection, confirmedCodeSelection, handleConfirmFailCodeSelection, onClose, onConfirmFailCode]);

    /**
     * Handle the section select change event. Extracts the value string and forwards to the hook.
     *
     * @param event - the native select change event
     */
    const handleSectionSelectChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            handleSectionChange(event.target.value);
        },
        [handleSectionChange]
    );

    /**
     * Handle the subsection select change event. Extracts the value string and forwards to the hook.
     *
     * @param event - the native select change event
     */
    const handleSubsectionSelectChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            handleSubsectionChange(event.target.value);
        },
        [handleSubsectionChange]
    );

    /**
     * Handle a fail code radio button press. Forwards the selected fail code to the hook.
     *
     * @param failCode - the fail code that was pressed
     */
    const handleFailCodeButtonPress = useCallback(
        (failCode: (typeof partFailCodeData)[number]) => {
            handleFailCodeSelect(failCode);
        },
        [handleFailCodeSelect]
    );

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>{t('modals:failCodePartSearch.title', 'Fail Code Search')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {!confirmedCodeSelection && (
                    <>
                        <Row>
                            <Col>
                                <AsyncTypeahead
                                    filterBy={filterByPassthrough}
                                    id="fail-code-search"
                                    isLoading={isLoading}
                                    labelKey={getPartLabel}
                                    minLength={2}
                                    onChange={handleTypeaheadChange}
                                    onInputChange={handleInputChange}
                                    onSearch={handlePartSearch}
                                    options={partOptionData}
                                    placeholder={t('modals:failCodePartSearch.searchPlacehodler', 'Search')}
                                    ref={searchInputRef as Ref<AsyncTypeahead<FailCodePart>>}
                                    renderMenu={renderPartSearchMenu}
                                    selected={selectedPartOption ? [selectedPartOption] : []}
                                    useCache={false}
                                />
                            </Col>
                        </Row>

                        <hr className="my-4" />
                    </>
                )}

                <Row>
                    {!confirmedCodeSelection ? (
                        <>
                            {!selectedPartOption ? (
                                <Col>
                                    <Typography color="textDarkSecondary" variant="lead">
                                        {t(
                                            'modals:failCodePartSearch.instructions.title',
                                            'Use the search bar above to find a fail code by entering any of the following:'
                                        )}
                                    </Typography>

                                    <div className="mt-2">
                                        <Typography color="textDarkSecondary" variant="lead">
                                            {t(
                                                'modals:failCodePartSearch.instructions.steps',
                                                '1. Part number\r\n2. Part description\r\n'
                                            )}
                                        </Typography>
                                    </div>
                                </Col>
                            ) : (
                                <Col>
                                    {partFailCodeData.length > 1 && (
                                        <>
                                            <div className="mb-4">
                                                <Typography color="textDarkSecondary" variant="lead">
                                                    {t(
                                                        'techline:failCodePartSearch.multiplePartMatchInstructions',
                                                        'Multiple matching fail codes have been found for Part #{{partNumber}} - {{partDescription}}. Would you like to add the matching Fail Code?',
                                                        { ...selectedPartOption }
                                                    )}
                                                </Typography>
                                            </div>
                                            <div className="mb-4">
                                                <Label>
                                                    {t(
                                                        'modals:failCodePartSearch.chooseFailCodeLabel',
                                                        'Please choose the correct Fail Code below:'
                                                    )}
                                                </Label>
                                                {partFailCodeData.map((partFailCode) => (
                                                    <ListButton
                                                        key={partFailCode.failCode}
                                                        // eslint-disable-next-line react/jsx-no-bind -- closure required per-item in .map()
                                                        onPress={() => handleFailCodeButtonPress(partFailCode)}
                                                        radio
                                                        rightIcon={null}
                                                        selected={
                                                            selectedPartFailCode?.failCode === partFailCode.failCode
                                                        }
                                                        title={`${partFailCode.failCode} - ${partFailCode.description}`}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    {partFailCodeData.length < 1 && (
                                        <div className="mb-4">
                                            <Typography color="textDarkSecondary" variant="lead">
                                                {t(
                                                    'modals:failCodePartSearch.noFailCodeMatch',
                                                    'No Fail Codes matched for Part  #{{ partNumber }} - {{partDescription}} for this VIN.',
                                                    { ...selectedPartOption }
                                                )}
                                            </Typography>
                                        </div>
                                    )}
                                </Col>
                            )}
                        </>
                    ) : (
                        <Col>
                            {isOnlyMatch && (
                                <div className="mb-4">
                                    <Typography color="textDarkSecondary" variant="lead">
                                        {t(
                                            'techline:failCodePartSearch.singleFailCodeMatch',
                                            'A matching fail code has been found for Part #{{partNumber}} - {{partDescription}}. Would you like to add the matching Fail Code?',
                                            { ...selectedPartOption }
                                        )}
                                    </Typography>
                                </div>
                            )}

                            {showSectionSelects && (
                                <div className="mb-4">
                                    <Typography color="textDarkSecondary" variant="lead">
                                        {autoSelectedSectionName
                                            ? t(
                                                  'modals:failCodePartSearch.multipleSubsectionMatchInstructions',
                                                  'Please select the subsection below to specify:'
                                              )
                                            : t(
                                                  'modals:failCodePartSearch.multipleMatchInstructions',
                                                  'Please select the section and subsection below to specify:'
                                              )}
                                    </Typography>
                                </div>
                            )}

                            {selectedPartFailCode && (
                                <div className="d-flex flex-column">
                                    <FailCodeDetails
                                        failCode={selectedPartFailCode}
                                        failCodeSectionName={autoSelectedSectionName}
                                        failCodeSubsectionName={autoSelectedSubsectionName}
                                        matchingPart={selectedPartOption}
                                        possibleMatch={isOnlyMatch}
                                    />
                                </div>
                            )}

                            {showSectionSelects && (
                                <>
                                    {!autoSelectedSectionName && (
                                        <div className="my-4">
                                            <Label>{t('modals:failCodePartSearch.sectionLabel', 'Section')}</Label>
                                            <Select
                                                onChange={handleSectionSelectChange}
                                                options={sectionOptionData}
                                                value={JSON.stringify(selectedFailCodeSection)}
                                            />
                                        </div>
                                    )}

                                    <Label>{t('modals:failCodePartSearch.subsectionLabel', 'Subsection')}</Label>
                                    <Select
                                        disabled={!selectedFailCodeSection}
                                        onChange={handleSubsectionSelectChange}
                                        options={subsectionOptionData}
                                        value={JSON.stringify(selectedFailCodeSubsection)}
                                    />
                                </>
                            )}
                        </Col>
                    )}
                </Row>
            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button
                    iconLeft={<Icon color="blueOne" name="chevron-left" />}
                    onPress={handleBackButtonClick}
                    variant="ghost-blue"
                >
                    {t('modals:failCodePartSearch.actions.back', 'Back')}
                </Button>

                <div className={classes.buttonRow}>
                    <Button onPress={onClose} variant="link-blue">
                        <Typography color="blueOne">{t('qmr:sections:failCode.cancel')}</Typography>
                    </Button>

                    <Button
                        disabled={confirmedCodeSelection ? isAddDisabled : isSelectDisabled}
                        onPress={handleSubmitButtonClick}
                    >
                        {confirmedCodeSelection
                            ? t('modals:failCodePartSearch.actions.add', 'Add Fail Code')
                            : t('modals:failCodePartSearch.actions.selectFailCode', 'Select Fail Code')}
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}

/**
 * Passthrough filter function for the AsyncTypeahead. Returns true for all items because
 * server-side filtering is already applied; client-side filtering would remove valid results.
 *
 * @returns true always
 */
function filterByPassthrough(): boolean {
    return true;
}

/**
 * Extract a display label from a fail code part for the typeahead.
 *
 * @param option - the fail code part option
 * @returns a formatted string like "12345 - Part description"
 */
function getPartLabel(option: FailCodePart): string {
    return `${option.partNumber} - ${option.partDescription}`;
}

/**
 * Custom render function for the AsyncTypeahead dropdown menu. Groups results by type
 * (currently only "Parts") and renders each group with a header and highlighted matches.
 *
 * @param results - the typeahead result items
 * @param menuProps - props to spread onto the Menu component
 * @param state - the typeahead state containing the current search text
 * @returns the rendered menu JSX
 */
function renderPartSearchMenu(
    results: FailCodePart[],
    menuProps: TypeaheadMenuProps<FailCodePart>,
    state: { /** The current search input text. */ text: string }
) {
    let index = 0;

    // Group results by type, filtering out pagination objects injected by the typeahead library
    const sections = groupBy(results, (item: FailCodePart) => {
        if (item.partNumber) {
            return 'Parts';
        }
        return 'unknown';
    });

    const items = Object.keys(sections)
        .filter((name) => name !== 'unknown')
        .sort()
        .map((sectionName) => (
            <Fragment key={sectionName}>
                {index !== 0 && <Menu.Divider />}

                <Menu.Header>
                    <Typography variant="label">{sectionName}</Typography>
                </Menu.Header>

                {sections[sectionName].map((item: FailCodePart) => {
                    const menuItem = (
                        <MenuItem key={index} option={item} position={index}>
                            <Highlighter search={state.text}>
                                {`${item.partNumber} - ${item.partDescription}`}
                            </Highlighter>
                        </MenuItem>
                    );

                    index += 1;
                    return menuItem;
                })}
            </Fragment>
        ));

    return <Menu {...menuProps}>{items}</Menu>;
}
